import { useCallback, useRef, useState } from "react";

import { useGlobalContext } from "../Context/GlobalContext";

/* =====================================================
   CONSTANTS
===================================================== */

const DEFAULT_PLATFORM = "WEB";
const DEFAULT_CURRENCY = "INR";
const DEFAULT_ORDER_TYPE = "home_delivery";

/* =====================================================
   HELPERS
===================================================== */

const normalizeTenDigitMobile = (mobileNumber) => {
  const digits = String(mobileNumber || "").replace(/\D/g, "");

  if (digits.length >= 10) {
    return digits.slice(-10);
  }

  return digits;
};

const cleanString = (value, fallbackValue = "") => {
  const finalValue = String(value ?? "").trim();

  return finalValue || fallbackValue;
};

const isMobileLikeValue = (value) => {
  const cleanValue = String(value ?? "").trim();
  const digits = cleanValue.replace(/\D/g, "");

  if (!cleanValue) {
    return false;
  }

  return (
    digits.length >= 10 &&
    digits.length === cleanValue.replace(/\s/g, "").length
  );
};

const getStoredCustomerName = () => {
  try {
    const firstName = cleanString(sessionStorage.getItem("first_name"));
    const lastName = cleanString(sessionStorage.getItem("last_name"));

    const fullName = `${firstName} ${lastName}`.trim();

    if (fullName && !isMobileLikeValue(fullName)) {
      return fullName;
    }

    const storedUserValue = sessionStorage.getItem("chaafoUser");

    if (storedUserValue) {
      const storedUser = JSON.parse(storedUserValue);

      const storedFirstName = cleanString(
        storedUser?.first_name || storedUser?.firstName,
      );

      const storedLastName = cleanString(
        storedUser?.last_name || storedUser?.lastName,
      );

      const storedFullName = `${storedFirstName} ${storedLastName}`.trim();

      if (storedFullName && !isMobileLikeValue(storedFullName)) {
        return storedFullName;
      }
    }
  } catch (error) {
    console.error("Unable to read customer name from session:", error);
  }

  return "";
};

const resolveCustomerName = (custName) => {
  const providedName = cleanString(custName);

  if (providedName && !isMobileLikeValue(providedName)) {
    return providedName;
  }

  return getStoredCustomerName() || "Customer";
};

const normalizeMobileNumber = (mobileNumber) => {
  const digits = String(mobileNumber || "").replace(/\D/g, "");

  if (digits.length === 10) {
    return `91${digits}`;
  }

  if (digits.length === 12 && digits.startsWith("91")) {
    return digits;
  }

  if (digits.length === 13 && digits.startsWith("091")) {
    return digits.slice(1);
  }

  return digits;
};

const normalizeOrderType = (orderType) => {
  const normalizedValue = String(orderType || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

  if (normalizedValue === "homedelivery" || normalizedValue === "delivery") {
    return "home_delivery";
  }

  if (normalizedValue === "dinein" || normalizedValue === "dining") {
    return "dinein";
  }

  if (normalizedValue === "takeaway") {
    return "takeaway";
  }

  return cleanString(orderType, DEFAULT_ORDER_TYPE);
};

const getResponseObject = (value) => {
  if (Array.isArray(value)) {
    return value[0] || {};
  }

  if (value && typeof value === "object") {
    return value;
  }

  return {};
};

const getResponseArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  return [];
};

const stringifyCustomization = (customization) => {
  if (!customization) {
    return "{}";
  }

  if (typeof customization === "string") {
    return customization.trim() || "{}";
  }

  try {
    return JSON.stringify(customization);
  } catch {
    return "{}";
  }
};

const getSelectedPrice = (item) => {
  if (item?.selectedPrice && typeof item.selectedPrice === "object") {
    return item.selectedPrice;
  }

  if (item?.priceData && typeof item.priceData === "object") {
    return item.priceData;
  }

  if (Array.isArray(item?.prices) && item.prices.length > 0) {
    return item.prices[0] || {};
  }

  return {};
};

const getItemPrice = (item, selectedPrice) => {
  const discountPrice = Number(
    selectedPrice?.discount_price ||
      selectedPrice?.discountPrice ||
      item?.discount_price ||
      item?.discountPrice ||
      0,
  );

  const regularPrice = Number(
    selectedPrice?.price ||
      selectedPrice?.item_price ||
      item?.itemPrice ||
      item?.item_price ||
      item?.price ||
      0,
  );

  if (
    Number.isFinite(discountPrice) &&
    discountPrice > 0 &&
    discountPrice < regularPrice
  ) {
    return String(discountPrice);
  }

  return String(Number.isFinite(regularPrice) ? regularPrice : 0);
};

const normalizeCartItem = (item) => {
  const selectedPrice = getSelectedPrice(item);

  const productId = cleanString(
    item?.productId ||
      item?.product_id ||
      item?.item_id ||
      item?.itemId ||
      item?.id,
  );

  const productName = cleanString(
    item?.productName ||
      item?.product_name ||
      item?.item_name ||
      item?.itemName ||
      item?.actualName,
  );

  const actualName = cleanString(
    item?.actualName ||
      item?.actual_name ||
      item?.item_name ||
      item?.itemName ||
      productName,
  );

  const sizeId = cleanString(
    item?.sizeId ||
      item?.size_id ||
      item?.selectedSize ||
      selectedPrice?.size ||
      selectedPrice?.size_id,
    "Regular",
  );

  const sizeWords = cleanString(
    item?.sizeWords ||
      item?.size_words ||
      selectedPrice?.sizeWords ||
      selectedPrice?.size_words ||
      sizeId,
    "Regular",
  );

  const itemImage = cleanString(
    item?.itemImage ||
      item?.item_image ||
      item?.photo ||
      item?.image ||
      item?.image_url,
  );

  const quantity = Math.max(0, Number(item?.quantity || item?.qty || 0));

  const hasVarient = cleanString(
    item?.hasVarient ||
      item?.hasVariant ||
      item?.has_varient ||
      item?.has_variant,
    sizeId ? "Y" : "N",
  ).toUpperCase();

  return {
    productId,
    sizeId,
    productName,
    actualName,
    customization: stringifyCustomization(
      item?.customization ||
        item?.customizations ||
        item?.selectedCustomizations,
    ),
    hasVarient,
    itemImage,
    itemPrice: getItemPrice(item, selectedPrice),
    quantity: String(quantity),
    sizeWords,
  };
};

const normalizeCartItems = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map(normalizeCartItem)
    .filter(
      (item) => item.productId && item.productName && Number(item.quantity) > 0,
    );
};

/* =====================================================
   CART HOOK
===================================================== */

export const useCart = () => {
  const { api } = useGlobalContext();

  const [cartLoading, setCartLoading] = useState(false);
  const [cartError, setCartError] = useState("");
  const [currentStep, setCurrentStep] = useState("");

  const [orderId, setOrderId] = useState("");
  const [restaurantName, setRestaurantName] = useState("");

  const [cartCalculation, setCartCalculation] = useState(null);
  const [cartDetails, setCartDetails] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [coupons, setCoupons] = useState([]);

  const [placingOrder, setPlacingOrder] = useState(false);
  const [placeOrderError, setPlaceOrderError] = useState("");
  const [paymentIntentData, setPaymentIntentData] = useState(null);

  const placingOrderRef = useRef(false);

  /* =====================================================
     COMMON POST REQUEST
  ===================================================== */

  const postRequest = useCallback(
    async (url, payload, fallbackErrorMessage) => {
      try {
        const response = await api.post(url, payload);
        const responseData = response?.data || {};

        if (
          responseData?.responseCode !== undefined &&
          String(responseData.responseCode) !== "00"
        ) {
          throw new Error(
            responseData?.responseMessage || fallbackErrorMessage,
          );
        }

        return responseData;
      } catch (error) {
        const apiMessage =
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          fallbackErrorMessage;

        throw new Error(apiMessage);
      }
    },
    [api],
  );

  /* =====================================================
     1. GENERATE ORDER NUMBER
  ===================================================== */

  const generateOrderNumber = useCallback(
    async ({
      userid,
      custMobile,
      orderType = DEFAULT_ORDER_TYPE,
      platform = DEFAULT_PLATFORM,
    }) => {
      setCurrentStep("generate_order_number");

      const cleanUserId = cleanString(userid);
      const cleanMobile = normalizeMobileNumber(custMobile);
      const cleanOrderType = normalizeOrderType(orderType);
      const cleanPlatform = cleanString(platform, DEFAULT_PLATFORM);

      if (!cleanUserId) {
        throw new Error(
          "Restaurant user ID is required to generate order number.",
        );
      }

      if (!cleanMobile) {
        throw new Error(
          "Customer mobile number is required to generate order number.",
        );
      }

      const payload = {
        platform: cleanPlatform,
        custMobile: cleanMobile,
        orderType: cleanOrderType,
        userid: cleanUserId,
      };

      const response = await postRequest(
        "/Order/v2.0/generateOrderNumber",
        payload,
        "Unable to generate order number.",
      );

      const responseObject = getResponseObject(
        response?.data || response?.response,
      );

      const generatedOrderId = cleanString(
        responseObject?.orderId ||
          responseObject?.order_id ||
          responseObject?.orderid,
      );

      const generatedRestaurantName = cleanString(
        responseObject?.restaurantName || responseObject?.restaurant_name,
      );

      if (!generatedOrderId) {
        throw new Error("Order ID was not received from generate order API.");
      }

      setOrderId(generatedOrderId);
      setRestaurantName(generatedRestaurantName);

      return {
        orderId: generatedOrderId,
        restaurantName: generatedRestaurantName,
        response,
        payload,
      };
    },
    [postRequest],
  );

  /* =====================================================
     2. ADD TO CART
  ===================================================== */

  const addToCart = useCallback(
    async ({
      orderId: generatedOrderId,
      userid,
      custMobile,
      custName,
      items,
      currency = DEFAULT_CURRENCY,
    }) => {
      setCurrentStep("add_to_cart");

      const cleanOrderId = cleanString(generatedOrderId);
      const cleanUserId = cleanString(userid);
      const cleanMobile = normalizeMobileNumber(custMobile);
      const cleanCustomerName = resolveCustomerName(custName);
      const cleanCurrency = cleanString(currency, DEFAULT_CURRENCY);

      const normalizedItems = normalizeCartItems(items);

      if (!cleanOrderId) {
        throw new Error("Order ID is required to add items to cart.");
      }

      if (!cleanUserId) {
        throw new Error("Restaurant user ID is required to add items to cart.");
      }

      if (!cleanMobile) {
        throw new Error(
          "Customer mobile number is required to add items to cart.",
        );
      }

      if (normalizedItems.length === 0) {
        throw new Error("Please add at least one valid item to the cart.");
      }

      const payload = {
        currency: cleanCurrency,
        custMobile: cleanMobile,
        custName: cleanCustomerName || "Customer",
        items: normalizedItems,
        orderId: cleanOrderId,
        userid: cleanUserId,
      };

      const response = await postRequest(
        "/Order/addToCart",
        payload,
        "Unable to add items to cart.",
      );

      return {
        response,
        payload,
        items: normalizedItems,
      };
    },
    [postRequest],
  );

  /* =====================================================
     3. CALCULATE DELIVERY CHARGES AND DISCOUNT
  ===================================================== */

  const calculateDeliveryChargesAndDiscount = useCallback(
    async ({ orderId: generatedOrderId, orderType = DEFAULT_ORDER_TYPE }) => {
      setCurrentStep("calculate_delivery_charges_and_discount");

      const cleanOrderId = cleanString(generatedOrderId);
      const cleanOrderType = normalizeOrderType(orderType);

      if (!cleanOrderId) {
        throw new Error("Order ID is required to calculate cart amount.");
      }

      const payload = {
        orderId: cleanOrderId,
        orderType: cleanOrderType,
      };

      const response = await postRequest(
        "/Order/v1.0/calculateDeliveryChargesAndDiscount",
        payload,
        "Unable to calculate delivery charges.",
      );

      const calculationData = getResponseObject(
        response?.response || response?.data,
      );

      setCartCalculation(calculationData);

      return {
        calculation: calculationData,
        response,
        payload,
      };
    },
    [postRequest],
  );

  /* =====================================================
     4. GET CART DETAILS
  ===================================================== */

  const getCartDetails = useCallback(
    async ({ orderId: generatedOrderId, orderType = DEFAULT_ORDER_TYPE }) => {
      setCurrentStep("get_cart_details");

      const cleanOrderId = cleanString(generatedOrderId);
      const cleanOrderType = normalizeOrderType(orderType);

      if (!cleanOrderId) {
        throw new Error("Order ID is required to get cart details.");
      }

      const payload = {
        orderType: cleanOrderType,
        orderid: cleanOrderId,
      };

      const response = await postRequest(
        "/Order/v1.0/getCartDetails",
        payload,
        "Unable to fetch cart details.",
      );

      const cartResponse = getResponseArray(response?.response);

      setCartDetails(cartResponse);

      return {
        cartDetails: cartResponse,
        cart: cartResponse[0] || null,
        response,
        payload,
      };
    },
    [postRequest],
  );

  /* =====================================================
     5. GET CATALOGUE ITEM SUGGESTIONS
  ===================================================== */

  const getCatalogItemsSuggestions = useCallback(
    async ({ userid }) => {
      const cleanUserId = cleanString(userid);

      if (!cleanUserId) {
        throw new Error("Restaurant user ID is required to get suggestions.");
      }

      const payload = {
        user_id: cleanUserId,
      };

      const response = await postRequest(
        "/Order/getCatalogItemsSuggestions",
        payload,
        "Unable to fetch item suggestions.",
      );

      const suggestionResponse = getResponseObject(response?.response);
      const suggestionItems = getResponseArray(suggestionResponse?.suggestions);

      setSuggestions(suggestionItems);

      return {
        suggestions: suggestionItems,
        response,
        payload,
      };
    },
    [postRequest],
  );

  /* =====================================================
     6. GET COUPON CODES
  ===================================================== */

  const getCouponCodes = useCallback(
    async ({ userid }) => {
      const cleanUserId = cleanString(userid);

      if (!cleanUserId) {
        throw new Error("Restaurant user ID is required to get coupons.");
      }

      const payload = {
        userid: cleanUserId,
      };

      const response = await postRequest(
        "/Order/v1.0/getCouponCodes",
        payload,
        "Unable to fetch coupon codes.",
      );

      const couponResponse = getResponseArray(response?.response);

      setCoupons(couponResponse);

      return {
        coupons: couponResponse,
        response,
        payload,
      };
    },
    [postRequest],
  );

  /* =====================================================
     CREATE CART SEQUENTIALLY
  ===================================================== */

  const createCartSequentially = useCallback(
    async ({
      userid,
      couponUserId,
      custMobile,
      custName,
      items,
      orderType = DEFAULT_ORDER_TYPE,
      platform = DEFAULT_PLATFORM,
      currency = DEFAULT_CURRENCY,
    }) => {
      setCartLoading(true);
      setCartError("");
      setCurrentStep("starting");

      setOrderId("");
      setRestaurantName("");
      setCartCalculation(null);
      setCartDetails([]);
      setSuggestions([]);
      setCoupons([]);

      try {
        const cleanUserId = cleanString(userid);
        const finalCouponUserId = cleanString(couponUserId, cleanUserId);
        const cleanOrderType = normalizeOrderType(orderType);

        const orderResult = await generateOrderNumber({
          userid: cleanUserId,
          custMobile,
          orderType: cleanOrderType,
          platform,
        });

        const generatedOrderId = orderResult.orderId;

        const addToCartResult = await addToCart({
          orderId: generatedOrderId,
          userid: cleanUserId,
          custMobile,
          custName,
          items,
          currency,
        });

        const optionalRequestsPromise = Promise.allSettled([
          getCatalogItemsSuggestions({
            userid: cleanUserId,
          }),
          getCouponCodes({
            userid: finalCouponUserId,
          }),
        ]);

        const calculationResult = await calculateDeliveryChargesAndDiscount({
          orderId: generatedOrderId,
          orderType: cleanOrderType,
        });

        const cartDetailsResult = await getCartDetails({
          orderId: generatedOrderId,
          orderType: cleanOrderType,
        });

        const optionalResults = await optionalRequestsPromise;

        const suggestionsResult =
          optionalResults[0]?.status === "fulfilled"
            ? optionalResults[0].value
            : {
                suggestions: [],
                response: null,
              };

        const couponsResult =
          optionalResults[1]?.status === "fulfilled"
            ? optionalResults[1].value
            : {
                coupons: [],
                response: null,
              };

        if (optionalResults[0]?.status === "rejected") {
          console.warn(
            "Suggestions API failed, but checkout will continue:",
            optionalResults[0].reason,
          );
          setSuggestions([]);
        }

        if (optionalResults[1]?.status === "rejected") {
          console.warn(
            "Coupon API failed, but checkout will continue:",
            optionalResults[1].reason,
          );
          setCoupons([]);
        }

        setCurrentStep("completed");

        return {
          success: true,
          orderId: generatedOrderId,
          restaurantName: orderResult.restaurantName,
          addedItems: addToCartResult.items,
          calculation: calculationResult.calculation,
          cartDetails: cartDetailsResult.cartDetails,
          cart: cartDetailsResult.cart,
          suggestions: suggestionsResult?.suggestions || [],
          coupons: couponsResult?.coupons || [],
          responses: {
            generateOrderNumber: orderResult.response,
            addToCart: addToCartResult.response,
            calculateDeliveryChargesAndDiscount: calculationResult.response,
            getCartDetails: cartDetailsResult.response,
            getCatalogItemsSuggestions: suggestionsResult?.response || null,
            getCouponCodes: couponsResult?.response || null,
          },
        };
      } catch (error) {
        console.error("Checkout preparation failed:", error);

        const message = error?.message || "Unable to complete cart process.";

        setCartError(message);
        setCurrentStep("failed");

        return {
          success: false,
          message,
          error,
        };
      } finally {
        setCartLoading(false);
      }
    },
    [
      generateOrderNumber,
      addToCart,
      calculateDeliveryChargesAndDiscount,
      getCartDetails,
      getCatalogItemsSuggestions,
      getCouponCodes,
    ],
  );

  /* =====================================================
     REFRESH CART
  ===================================================== */

  const refreshCart = useCallback(
    async ({
      orderId: existingOrderId,
      userid,
      couponUserId,
      orderType = DEFAULT_ORDER_TYPE,
    }) => {
      setCartLoading(true);
      setCartError("");

      try {
        const cleanOrderId = cleanString(existingOrderId);
        const cleanUserId = cleanString(userid);
        const finalCouponUserId = cleanString(couponUserId, cleanUserId);
        const cleanOrderType = normalizeOrderType(orderType);

        const optionalRequestsPromise = Promise.allSettled([
          getCatalogItemsSuggestions({
            userid: cleanUserId,
          }),
          getCouponCodes({
            userid: finalCouponUserId,
          }),
        ]);

        const calculationResult = await calculateDeliveryChargesAndDiscount({
          orderId: cleanOrderId,
          orderType: cleanOrderType,
        });

        const cartDetailsResult = await getCartDetails({
          orderId: cleanOrderId,
          orderType: cleanOrderType,
        });

        const optionalResults = await optionalRequestsPromise;

        const suggestionsResult =
          optionalResults[0]?.status === "fulfilled"
            ? optionalResults[0].value
            : {
                suggestions: [],
                response: null,
              };

        const couponsResult =
          optionalResults[1]?.status === "fulfilled"
            ? optionalResults[1].value
            : {
                coupons: [],
                response: null,
              };

        if (optionalResults[0]?.status === "rejected") {
          console.warn("Suggestions API failed:", optionalResults[0].reason);
          setSuggestions([]);
        }

        if (optionalResults[1]?.status === "rejected") {
          console.warn("Coupon API failed:", optionalResults[1].reason);
          setCoupons([]);
        }

        setCurrentStep("completed");

        return {
          success: true,
          orderId: cleanOrderId,
          calculation: calculationResult.calculation,
          cartDetails: cartDetailsResult.cartDetails,
          cart: cartDetailsResult.cart,
          suggestions: suggestionsResult.suggestions,
          coupons: couponsResult.coupons,
        };
      } catch (error) {
        const message = error?.message || "Unable to refresh cart.";

        setCartError(message);
        setCurrentStep("failed");

        return {
          success: false,
          message,
          error,
        };
      } finally {
        setCartLoading(false);
      }
    },
    [
      calculateDeliveryChargesAndDiscount,
      getCartDetails,
      getCatalogItemsSuggestions,
      getCouponCodes,
    ],
  );

  /* =====================================================
     PLACE ORDER
  ===================================================== */

  const placeOrder = useCallback(
    async ({
      userid,
      orderId: currentOrderId,
      orderFrom = "",
      is_scheduled = "",
      scheduled_date = "",
      note = "",
    }) => {
      setCurrentStep("place_order");

      const cleanUserId = cleanString(userid);
      const cleanOrderId = cleanString(currentOrderId);

      if (!cleanUserId) {
        throw new Error("Restaurant user ID is required to place order.");
      }

      if (!cleanOrderId) {
        throw new Error("Order ID is required to place order.");
      }

      const payload = {
        userid: cleanUserId,
        orderId: cleanOrderId,
        orderFrom: cleanString(orderFrom),
        is_scheduled: cleanString(is_scheduled),
        scheduled_date: cleanString(scheduled_date),
        note: cleanString(note),
      };

      const response = await postRequest(
        "/Order/placeOrderV5",
        payload,
        "Unable to place order.",
      );

      return {
        response,
        payload,
        data: getResponseObject(
          response?.data || response?.response || response,
        ),
      };
    },
    [postRequest],
  );

  /* =====================================================
     UPDATE ORDER TYPE
  ===================================================== */

  const updateOrderType = useCallback(
    async ({ custMobile }) => {
      setCurrentStep("update_order_type");

      const cleanMobile = normalizeMobileNumber(custMobile);

      if (!cleanMobile) {
        throw new Error(
          "Customer mobile number is required to update order type.",
        );
      }

      const payload = {
        custmobile: cleanMobile,
      };

      const response = await postRequest(
        "/Order/v1.0/updateOrderType",
        payload,
        "Unable to update order type.",
      );

      return {
        response,
        payload,
        data: getResponseObject(
          response?.data || response?.response || response,
        ),
      };
    },
    [postRequest],
  );

  /* =====================================================
     GENERATE PAYMENT INTENT V2
  ===================================================== */

  const generatePaymentIntentV2 = useCallback(
    async ({
      userid,
      orderId: currentOrderId,
      custName,
      custMobile,
      isOnline = "Y",
      charges,
    }) => {
      setCurrentStep("generate_payment_intent");

      const cleanUserId = cleanString(userid);

      const cleanOrderId = cleanString(currentOrderId);

      const cleanCustomerName = resolveCustomerName(custName);

      const cleanMobile = normalizeTenDigitMobile(custMobile);

      const cleanIsOnline = cleanString(isOnline, "Y");

      const cleanCharges = cleanString(charges);

      if (!cleanUserId) {
        throw new Error(
          "Restaurant user ID is required to generate payment link.",
        );
      }

      if (!cleanOrderId) {
        throw new Error("Order ID is required to generate payment link.");
      }

      if (cleanMobile.length !== 10) {
        throw new Error("Valid 10-digit customer mobile number is required.");
      }

      if (!cleanCharges || Number(cleanCharges) <= 0) {
        throw new Error("Valid payment amount is required.");
      }

      const payload = {
        userid: cleanUserId,

        orderId: cleanOrderId,

        custName: cleanCustomerName || "Customer",

        custMobile: cleanMobile,

        isOnline: cleanIsOnline,

        charges: "",
      };

      console.log("💳 Generate Payment Intent V2 Payload:", payload);

      const response = await postRequest(
        "/Order/v2.0/generatePaymentIntentv2",
        payload,
        "Unable to generate payment link.",
      );

      console.log("💳 Generate Payment Intent V2 Response:", response);

      const paymentLink = cleanString(
        response?.response?.paymentLink ||
          response?.data?.paymentLink ||
          response?.paymentLink,
      );

      if (!paymentLink) {
        throw new Error(
          "Payment link was not received from payment intent API.",
        );
      }

      const paymentData = {
        paymentLink,

        responseCode: cleanString(response?.responseCode),

        responseMessage: cleanString(response?.responseMessage),
      };

      setPaymentIntentData(paymentData);

      return {
        success: true,

        paymentLink,

        data: paymentData,

        response,

        payload,
      };
    },
    [postRequest],
  );

  /* =====================================================
     PLACE ORDER SEQUENTIALLY

     Required order:
     1. placeOrder
     2. updateOrderType
     3. generatePaymentIntentV2
  ===================================================== */

  const placeOrderSequentially = useCallback(
    async ({
      userid,
      orderId: currentOrderId,

      custName,
      custMobile,

      isOnline = "Y",

      charges,

      orderFrom = "",
      is_scheduled = "",
      scheduled_date = "",
      note = "",
    }) => {
      if (placingOrderRef.current) {
        return {
          success: false,
          message: "Order request is already processing.",
        };
      }

      placingOrderRef.current = true;

      setPlacingOrder(true);

      setPlaceOrderError("");

      setPaymentIntentData(null);

      let activeStep = "place_order";

      try {
        // ===============================================
        // 1. PLACE ORDER
        // ===============================================

        setCurrentStep(activeStep);

        const placeOrderResult = await placeOrder({
          userid,

          orderId: currentOrderId,

          orderFrom,

          is_scheduled,

          scheduled_date,

          note,
        });

        if (!placeOrderResult?.response) {
          throw new Error("Place order API did not return a valid response.");
        }

        // ===============================================
        // 2. UPDATE ORDER TYPE
        // ===============================================

        activeStep = "update_order_type";

        setCurrentStep(activeStep);

        const updateOrderTypeResult = await updateOrderType({
          custMobile,
        });

        if (!updateOrderTypeResult?.response) {
          throw new Error(
            "Update order type API did not return a valid response.",
          );
        }

        // ===============================================
        // 3. GENERATE PAYMENT LINK
        // ===============================================

        activeStep = "generate_payment_intent";

        setCurrentStep(activeStep);

        const paymentIntentResult = await generatePaymentIntentV2({
          userid,

          orderId: currentOrderId,

          custName,

          custMobile,

          isOnline,

          charges,
        });

        const paymentLink = String(
          paymentIntentResult?.paymentLink ||
            paymentIntentResult?.data?.paymentLink ||
            "",
        ).trim();

        if (!paymentLink) {
          throw new Error("Payment URL was not received.");
        }

        setCurrentStep("order_completed");

        return {
          success: true,

          orderId: cleanString(currentOrderId),

          paymentLink,

          placeOrder: placeOrderResult,

          updateOrderType: updateOrderTypeResult,

          paymentIntent: paymentIntentResult,

          payment: {
            data: {
              paymentLink,
            },
          },
        };
      } catch (error) {
        console.error("Place order sequence failed:", {
          step: activeStep,

          message: error?.message,

          response: error?.response?.data,

          error,
        });

        const message =
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to place order.";

        setPlaceOrderError(message);

        setCurrentStep("order_failed");

        return {
          success: false,

          message,

          step: activeStep,

          error,
        };
      } finally {
        placingOrderRef.current = false;

        setPlacingOrder(false);
      }
    },
    [placeOrder, updateOrderType, generatePaymentIntentV2],
  );

  /* =====================================================
     RESET
  ===================================================== */

  const resetCartService = useCallback(() => {
    placingOrderRef.current = false;

    setCartLoading(false);
    setCartError("");
    setCurrentStep("");

    setOrderId("");
    setRestaurantName("");
    setCartCalculation(null);
    setCartDetails([]);
    setSuggestions([]);
    setCoupons([]);

    setPlacingOrder(false);
    setPlaceOrderError("");
    setPaymentIntentData(null);
  }, []);

  return {
    cartLoading,
    cartError,
    currentStep,

    orderId,
    restaurantName,

    cartCalculation,
    cartDetails,
    cart: cartDetails[0] || null,

    suggestions,
    coupons,

    placingOrder,
    placeOrderError,
    paymentIntentData,

    generateOrderNumber,
    addToCart,
    calculateDeliveryChargesAndDiscount,
    getCartDetails,
    getCatalogItemsSuggestions,
    getCouponCodes,

    createCartSequentially,
    refreshCart,
    resetCartService,
    normalizeCartItems,

    placeOrder,
    updateOrderType,
    generatePaymentIntentV2,
    placeOrderSequentially,
  };
};

export default useCart;
